import { getIn } from 'formik';
import { Box, TextField, useMediaQuery, useTheme } from '@mui/material';
import { tokens } from '../../../theme';

const AddressForm = ({
  type,
  values,
  touched,
  errors,
  handleBlur,
  handleChange,
  fieldSx = {},
}) => {
  const isNonMobile = useMediaQuery('(min-width:600px)');
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const formattedName = (field) => `${type}.${field}`;

  const formattedError = (field) =>
    Boolean(getIn(touched, formattedName(field)) && getIn(errors, formattedName(field)));

  const formattedHelper = (field) =>
    getIn(touched, formattedName(field)) && getIn(errors, formattedName(field));

const baseFieldSx = {
  '& .MuiInputBase-root': {
    color: 'white',
    backgroundColor: 'rgba(255,255,255,0.16)',
    borderRadius: '14px',
  },
  '& .MuiInputBase-input': {
    color: 'white',
    fontWeight: 600,
  },
  '& .MuiInputBase-input::placeholder': {
    color: 'rgba(255,255,255,0.65)',
    opacity: 1,
  },
  '& .MuiInputLabel-root': {
    color: 'rgba(255,255,255,0.78)',
    fontWeight: 600,
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: colors.greenAccent[300],
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(255,255,255,0.32)',
  },
  '& .MuiOutlinedInput-root:hover': {
    backgroundColor: 'rgba(255,255,255,0.20)',
  },
  '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: colors.greenAccent[500],
  },
  '& .MuiOutlinedInput-root.Mui-focused': {
    backgroundColor: 'rgba(255,255,255,0.22)',
  },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: colors.greenAccent[400],
    borderWidth: '2px',
  },
  '& .MuiFormHelperText-root': {
    color: colors.redAccent?.[300] || '#ff8a80',
    fontWeight: 700,
    marginLeft: 0,
  },
};

  const makeFieldSx = (gridColumn) => ({
    gridColumn,
    ...baseFieldSx,
    ...fieldSx,
  });

  const renderField = ({
    field,
    label,
    gridColumn,
    autoComplete,
    required = false,
  }) => (
    <TextField
      fullWidth
      required={required}
      type="text"
      label={label}
      onBlur={handleBlur}
      onChange={handleChange}
      value={values[field] || ''}
      name={formattedName(field)}
      error={formattedError(field)}
      helperText={formattedHelper(field)}
      autoComplete={autoComplete}
      sx={makeFieldSx(gridColumn)}
    />
  );

  return (
    <Box
      display="grid"
      gap="15px"
      gridTemplateColumns="repeat(4, minmax(0, 1fr))"
      sx={{
        '& > div': {
          gridColumn: isNonMobile ? undefined : 'span 4 !important',
        },
      }}
    >
      {renderField({
        field: 'firstName',
        label: 'First Name',
        gridColumn: 'span 2',
        autoComplete: 'given-name',
        required: true,
      })}

      {renderField({
        field: 'lastName',
        label: 'Last Name',
        gridColumn: 'span 2',
        autoComplete: 'family-name',
        required: true,
      })}

      {renderField({
        field: 'country',
        label: 'Country',
        gridColumn: 'span 4',
        autoComplete: 'country-name',
        required: true,
      })}

      {renderField({
        field: 'street1',
        label: 'Street Address',
        gridColumn: 'span 4',
        autoComplete: 'address-line1',
        required: true,
      })}

      {renderField({
        field: 'street2',
        label: 'Street Address 2 (optional)',
        gridColumn: 'span 4',
        autoComplete: 'address-line2',
      })}

      {renderField({
        field: 'city',
        label: 'City',
        gridColumn: 'span 2',
        autoComplete: 'address-level2',
        required: true,
      })}

      {renderField({
        field: 'state',
        label: 'State',
        gridColumn: 'span 1',
        autoComplete: 'address-level1',
        required: true,
      })}

      {renderField({
        field: 'zipCode',
        label: 'Zip Code',
        gridColumn: 'span 1',
        autoComplete: 'postal-code',
        required: true,
      })}
    </Box>
  );
};

export default AddressForm;